import { Col, notification } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { RangeValue } from 'rc-picker/lib/interface'

import { useFilters, useGeneral } from '@contexts'

import { DateFromTo } from '../StyledComponents'

interface DateFromToFilterProps {
  time: string
}
const TIME = {
  CURRENT: 'current',
  EXPIRED: 'expired',
}
const DateFromToFilter = ({ time }: DateFromToFilterProps) => {
  const { filters, setFilters, resetTriggered } = useFilters()
  const { t } = useTranslation()
  const { maxDaysRange, ticketToSearch, setTicketToSearch } = useGeneral()
  const startDate =
    time === TIME.CURRENT
      ? dayjs().startOf('d')
      : dayjs()
          .subtract(maxDaysRange + 1, 'd')
          .startOf('d')
  const endDate =
    time === TIME.EXPIRED ? dayjs().endOf('d') : dayjs().add(maxDaysRange, 'd').endOf('d')
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(startDate)
  const [dateTo, setDateTo] = useState<Dayjs | null>(endDate)
  const [rangePickerOpen, setRangePickerOpen] = useState<boolean>(false)

  useEffect(() => {
    if (!rangePickerOpen && dateFrom && !dateTo) {
      const fromRange = dateFrom?.clone()
      fromRange.add(maxDaysRange, 'd').startOf('d')
      if (time === TIME.CURRENT) {
        setDateTo(fromRange.endOf('d'))
      } else if (time === TIME.EXPIRED) {
        setDateTo(fromRange < endDate ? fromRange.endOf('d') : endDate)
      }
      handleSetFilters([dateFrom, fromRange.endOf('d')])
    } else if (!rangePickerOpen && !dateFrom && dateTo) {
      const toRange = dateTo?.clone()
      toRange.subtract(maxDaysRange, 'd').startOf('d')
      if (time === TIME.EXPIRED) {
        setDateFrom(toRange.startOf('d'))
      } else if (time === TIME.CURRENT) {
        setDateFrom(toRange > startDate ? toRange.endOf('d') : startDate)
      }
      handleSetFilters([toRange.startOf('d'), dateTo])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangePickerOpen])

  useEffect(() => {
    if (ticketToSearch) {
      setDateFrom(null)
      setDateTo(null)
    } else {
      setDateFrom(startDate)
      setDateTo(endDate)
      handleSetFilters([startDate, endDate])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketToSearch, resetTriggered])

  const onOpenChange = (open: boolean) => {
    if (open) {
      setRangePickerOpen(true)
      setDateFrom(null)
      setDateTo(null)
    } else {
      setRangePickerOpen(false)
      if (!dateFrom && !dateTo) {
        setDateFrom(startDate)
        setDateTo(endDate)
        handleSetFilters([startDate, endDate])
      }
    }
  }

  const handleSetFilters = (values: RangeValue<Dayjs>) => {
    if (values && values[0] !== null && values[1] !== null) {
      setFilters({
        ...filters,
        dateFrom: values[0].startOf('d').toDate(),
        dateTo: values[1].endOf('d').toDate(),
      })
    } else {
      notification.error({ message: t('globals.generics.apply.error'), duration: 4 })
    }
  }
  const onChange = (values: RangeValue<Dayjs>) => {
    if (values && values[0] !== null && values[1] !== null) {
      setDateFrom(values[0].startOf('d'))
      setDateTo(values[1].endOf('d'))
      handleSetFilters(values)
    }
  }
  const onCalendarChange = (values: RangeValue<Dayjs>): void => {
    setDateFrom(values ? values[0] && values[0].startOf('d') : null)
    setDateTo(values ? values[1] && values[1].endOf('d') : null)
  }
  const disableDate = (current: Dayjs) => {
    const fromRange = dateFrom?.clone()
    const toRange = dateTo?.clone()
    if (time === TIME.CURRENT) {
      return (
        current < dayjs().startOf('d') ||
        (fromRange ? current > fromRange.add(maxDaysRange, 'd').endOf('d') : false) ||
        (toRange ? current < toRange.subtract(maxDaysRange, 'd').startOf('d') : false)
      )
    }
    return (
      current > dayjs().endOf('d') ||
      (fromRange ? current > fromRange.add(maxDaysRange, 'd').startOf('d') : false) ||
      (toRange ? current < toRange.subtract(maxDaysRange, 'd').endOf('d') : false)
    )
  }
  return (
    <Col className="from-to-handler-container">
      <DateFromTo
        disabledDate={disableDate}
        format="dd L"
        value={[dateFrom, dateTo]}
        onCalendarChange={onCalendarChange}
        onChange={onChange}
        onOpenChange={onOpenChange}
        allowClear={false}
        onFocus={() => setTicketToSearch(undefined)}
      />
    </Col>
  )
}

export { DateFromToFilter, TIME }
